<template>
  <v-row class="nowrap" align="end" no-gutters>
    <v-col class="mr-4" cols="3">
      <label class="asc-filter-label">Y Axis Override:</label>
      <asc-autocomplete-selector
        v-model="unitSelection"
        :disabled="true"
        label="Unit"
        :items="filters"
        :min-width="125"
        component-type="single"
        no-array
      />
    </v-col>
    <v-col class="mr-4" cols="2">
      <asc-text-field-input
        v-model.number="minValue"
        class="asc-parameter-graph-filter--input"
        :disabled="unitNotSelected"
        label="Min"
        placeholder="#"
        type="number"
        update-on-blur
        hide-details
      />
    </v-col>
    <v-col class="mr-4" cols="2">
      <asc-text-field-input
        v-model.number="maxValue"
        class="asc-parameter-graph-filter--input"
        :disabled="unitNotSelected"
        label="Max"
        placeholder="#"
        type="number"
        update-on-blur
        hide-details
      />
    </v-col>
    <v-col cols="4">
      <div class="asc-parameter-graph-filter--buttons">
        <asc-accent-button
          class="asc-parameter-graph-filter--apply-button"
          button-text="APPLY"
          :on-click="handleApply"
          outline-only
          secondary
        />

        <asc-accent-button
          class="asc-parameter-graph-filter--reset-button"
          button-text="RESET"
          :on-click="handleReset"
          outline-only
          secondary
        />
      </div>
    </v-col>
  </v-row>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscTextFieldInput from '@/components/atoms/AscTextFieldInput'
import { debounceTime } from '@/utilities/appConsts'
import AscAutocompleteSelector from './AscAutocompleteSelector'

export default {
  name: 'AscParameterGraphFilter',

  components: {
    AscAccentButton,
    AscAutocompleteSelector,
    AscTextFieldInput,
  },

  props: {
    filters: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      maxValue: null,
      minValue: null,
      unitSelection:
        this.filters && this.filters.length > 0 ? this.filters[0].value : null,
      timeoutEmit: null,
      timeoutMin: null,
      timeoutMax: null,
    }
  },

  computed: {
    disabled() {
      return !this.filters.length || this.filters.length <= 1
    },

    unitNotSelected() {
      return this.unitSelection === null
    },
  },

  watch: {
    maxValue(newValue, oldValue) {
      newValue = Number(newValue)
      if (this.timeoutMax) clearTimeout(this.timeoutMax)
      this.timeoutMax = setTimeout(() => {
        if (newValue < this.minValue) {
          this.maxValue =
            oldValue < this.minValue
              ? this.filters[this.unitSelection].originalValue.max
              : oldValue
        } else {
          this.maxValue =
            newValue !== ''
              ? newValue
              : this.filters[this.unitSelection].originalValue.max
        }
        this.filters[this.unitSelection].max = Number(this.maxValue)
      }, debounceTime)
    },

    minValue(newValue, oldValue) {
      newValue = Number(newValue)
      if (this.timeoutMin) clearTimeout(this.timeoutMin)
      this.timeoutMin = setTimeout(() => {
        if (newValue > this.maxValue) {
          this.minValue =
            oldValue > this.maxValue
              ? this.filters[this.unitSelection].originalValue.min
              : oldValue
        } else {
          this.minValue =
            newValue !== ''
              ? newValue
              : this.filters[this.unitSelection].originalValue.min
        }
        this.filters[this.unitSelection].min = Number(this.minValue)
      }, debounceTime)
    },

    filters: {
      deep: true,
      handler(newValue) {
        this.filters = newValue
        if (this.filters && this.filters.length) {
          if (this.filters.length === 1) {
            this.unitSelection = this.filters[0].value
          }
          this.setMinAndMaxValue(this.unitSelection)
        }
      },
    },

    unitSelection(newValue) {
      this.unitSelection = newValue
      this.setMinAndMaxValue(this.unitSelection)
    },
  },

  mounted() {
    if (this.filters && this.filters.length && this.unitSelection >= 0) {
      this.setMinAndMaxValue(this.unitSelection)
    } else if (this.filters && this.filters.length && !this.unitSelection) {
      this.unitSelection = 0
      this.setMinAndMaxValue(this.unitSelection)
    }
  },

  methods: {
    emitChanges() {
      if (this.timeoutEmit) clearTimeout(this.timeoutEmit)
      this.timeoutEmit = setTimeout(() => {
        this.$emit('changes', this.filters[this.unitSelection])
      }, debounceTime)
    },

    handleApply() {
      this.emitChanges()
    },

    handleReset() {
      this.maxValue = this.filters[this.unitSelection].originalValue.max
      this.minValue = this.filters[this.unitSelection].originalValue.min
      this.filters[this.unitSelection].max = this.maxValue
      this.filters[this.unitSelection].min = this.minValue
      this.emitChanges()
    },

    setMinAndMaxValue(selection) {
      this.maxValue = this.filters[selection].max
      this.minValue = this.filters[selection].min
    },
  },
}
</script>

<style lang="scss" scoped>
.asc-parameter-graph-filter {
  &--buttons {
    display: flex;
  }

  &--apply-button {
    margin-right: 8px;
  }

  &--input {
    padding-top: 2px;
    min-height: 51px;
  }
}
</style>
